import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  IntegrationError,
  IntegrationErrorsPagination,
  IntegrationErrorsSummary,
} from '../../@types';
import SyncAgrogestaoService, {
  SyncAgrogestaoParams,
} from '../../services/sync-agrogestao';
import { PeriodoModo, StatusFilterValue } from './constants';
import { buildIntegrationErrorsRequestParams } from './utils';

type IntegrationErrorsParams = Omit<SyncAgrogestaoParams, 'page' | 'perPage'>;

interface PeriodoAplicado {
  modo: PeriodoModo;
  range: Date[];
}

interface IntegrationErrorsStore {
  totalErrorsCount: number;
  errors: IntegrationError[];
  summary: IntegrationErrorsSummary;
  pagination: IntegrationErrorsPagination | null;
  activeParams: IntegrationErrorsParams;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasLoaded: boolean;
  loadError: boolean;
  textInput: string;
  statusFilter: StatusFilterValue;
  periodoModoDraft: PeriodoModo;
  periodoCustomDraft: Date[];
  periodoAplicado: PeriodoAplicado;

  setTextInput: (text: string) => void;
  setStatusFilter: (status: StatusFilterValue) => void;
  setPeriodoModoDraft: (modo: PeriodoModo) => void;
  setPeriodoCustomDraft: (range: Date[]) => void;
  setPeriodoAplicado: (dados: PeriodoAplicado) => void;
  loadErrors: (params?: IntegrationErrorsParams) => Promise<void>;
  loadNextPage: () => Promise<void>;
  markErrorAsSyncing: (id: string) => void;
  clearFilters: () => void;
}

const initialFiltersState = {
  textInput: '',
  statusFilter: 'erro' as StatusFilterValue,
  periodoModoDraft: null as PeriodoModo,
  periodoCustomDraft: [] as Date[],
  periodoAplicado: {
    modo: null as PeriodoModo,
    range: [] as Date[],
  },
};

const initialState = {
  totalErrorsCount: 0,
  errors: [] as IntegrationError[],
  summary: {
    total: 0,
    byStatus: {},
  } as IntegrationErrorsSummary,
  pagination: null as IntegrationErrorsPagination | null,
  activeParams: {} as IntegrationErrorsParams,
  isLoading: false,
  isLoadingMore: false,
  hasLoaded: false,
  loadError: false,
  ...initialFiltersState,
};

const hasActiveFilters = (params: IntegrationErrorsParams) =>
  !!params.search || !!params.status || !!params.from || !!params.to;

const getCurrentFiltersParams = (
  state: IntegrationErrorsStore,
): IntegrationErrorsParams =>
  buildIntegrationErrorsRequestParams({
    textInput: state.textInput,
    statusFilter: state.statusFilter,
    periodoAplicado: state.periodoAplicado,
  });

const useIntegrationErrorsStore = create<IntegrationErrorsStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setTextInput: (text) => set({ textInput: text }),
      setStatusFilter: (status) => set({ statusFilter: status }),
      setPeriodoModoDraft: (modo) => set({ periodoModoDraft: modo }),
      setPeriodoCustomDraft: (range) => set({ periodoCustomDraft: range }),
      setPeriodoAplicado: (dados) => set({ periodoAplicado: dados }),
      loadErrors: async (params) => {
        const requestParams = params ?? getCurrentFiltersParams(get());

        set({
          isLoading: true,
          errors: [],
          pagination: null,
          activeParams: requestParams,
          loadError: false,
        });

        try {
          const response = await SyncAgrogestaoService.list({
            ...requestParams,
            page: 1,
          });

          set((state) => ({
            errors: response.data,
            summary: response.summary,
            pagination: response.pagination,
            hasLoaded: true,
            loadError: false,
            totalErrorsCount: hasActiveFilters(requestParams)
              ? state.totalErrorsCount
              : response.summary.byStatus.erro ?? 0,
          }));
        } catch (error) {
          set({ loadError: true });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      loadNextPage: async () => {
        const { pagination, isLoading, isLoadingMore, activeParams } = get();

        if (!pagination?.nextPage || isLoading || isLoadingMore) {
          return;
        }

        set({ isLoadingMore: true });

        try {
          const response = await SyncAgrogestaoService.list({
            ...activeParams,
            page: pagination.nextPage,
          });

          set((state) => ({
            errors: [...state.errors, ...response.data],
            pagination: response.pagination,
          }));
        } finally {
          set({ isLoadingMore: false });
        }
      },
      markErrorAsSyncing: (id) =>
        set((state) => ({
          errors: state.errors.map((item) =>
            item.id === id ? { ...item, status: 'processando' } : item,
          ),
          totalErrorsCount: Math.max(state.totalErrorsCount - 1, 0),
          summary: {
            ...state.summary,
            total: Math.max(state.summary.total - 1, 0),
            byStatus: {
              ...state.summary.byStatus,
              erro: Math.max((state.summary.byStatus.erro ?? 0) - 1, 0),
            },
          },
        })),
      clearFilters: () =>
        set({
          ...initialFiltersState,
          errors: [],
          pagination: null,
          activeParams: {},
        }),
    }),
    {
      name: 'integration-errors-filters',
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({
        textInput: state.textInput,
        statusFilter: state.statusFilter,
        periodoAplicado: state.periodoAplicado,
      }),
    },
  ),
);

export default useIntegrationErrorsStore;
